import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home, Package, FileText, Receipt, Users, History,
  Menu, X, ChevronLeft, ChevronRight, ShoppingCart, Plus, Settings
} from 'lucide-react';

const SalesLayout = ({ children }) => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Sales navigation links
  const salesNavLinks = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/sales/products', icon: Package, label: 'Products' },
    { path: '/sales/customers', icon: Users, label: 'Customers' },
    { path: '/sales/estimates', icon: FileText, label: 'Estimates' },
    { path: '/sales/invoices', icon: Receipt, label: 'Invoices' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="flex h-screen">
        {/* Sales Sidebar - Responsive */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-40
          transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 transition-transform duration-300 ease-in-out
          bg-[#0A5F38] border-r border-white/10 shadow-lg flex-shrink-0
          ${sidebarCollapsed ? 'w-20' : 'w-72'
          }
        `}
          style={{
            top: user?.isSandbox ? 'var(--sandbox-banner-height)' : '0',
            height: user?.isSandbox ? 'calc(100vh - var(--sandbox-banner-height))' : '100vh'
          }}
        >
          <div className="flex flex-col h-full">
            <div className="p-6 flex-shrink-0">
              {/* Sidebar Header */}
              <div className="flex items-center justify-between mb-8">
                {!sidebarCollapsed ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                      <img
                        src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                        alt="IDEALTRENDZ Logo"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-[#C5A57D] rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                        <span className="text-[#0A5F38] font-bold text-sm">IT</span>
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <Link to="/" className="text-xl font-bold text-white truncate transition-all duration-300">
                        IDEALTRENDZ
                      </Link>
                      <span className="text-sm font-semibold text-white/80 mt-1 uppercase tracking-wide">EMPLOYEE PORTAL</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
                      <img
                        src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                        alt="IDEALTRENDZ Logo"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-[#C5A57D] rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                        <span className="text-white font-bold text-xs">IT</span>
                      </div>
                    </div>
                  </div>
                )}
                <button
                  onClick={toggleSidebar}
                  className="group p-2 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                >
                  {sidebarCollapsed ? (
                    <ChevronRight className="h-4 w-4 text-white/70" />
                  ) : (
                    <ChevronLeft className="h-5 w-5 text-white/70" />
                  )}
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="space-y-2">
                {salesNavLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive(link.path)
                        ? 'bg-[#C5A57D] text-[#0A5F38] font-bold'
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      title={sidebarCollapsed ? link.label : ''}
                    >
                      <div className="flex items-center justify-center w-6 h-6 flex-shrink-0 relative z-10">
                        <Icon className={`h-5 w-5 ${isActive(link.path)
                          ? 'text-[#0A5F38]'
                          : 'text-white/80 group-hover:text-white'
                          }`} />
                      </div>
                      {!sidebarCollapsed && (
                        <span className="truncate">
                          {link.label}
                        </span>
                      )}

                      {sidebarCollapsed && (
                        <div className="absolute left-full ml-2 bg-gray-900 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                          {link.label}
                        </div>
                      )}

                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top Navbar - Mobile */}
          <div
            className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-sm sticky z-50"
            style={{
              top: user?.isSandbox ? 'var(--sandbox-banner-height)' : '0'
            }}
          >
            <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
              {/* Left side - Hamburger + Logo */}
              <div className="flex items-center gap-3">
                {/* Hamburger Menu - Mobile Only */}
                <button
                  onClick={() => {
                    console.log('Hamburger clicked, current state:', mobileMenuOpen);
                    setMobileMenuOpen(!mobileMenuOpen);
                  }}
                  className="lg:hidden p-2 rounded-lg hover:bg-white/80 transition-colors cursor-pointer z-50 relative"
                  aria-label="Toggle menu"
                  type="button"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6 text-slate-700 pointer-events-none" />
                  ) : (
                    <Menu className="h-6 w-6 text-slate-700 pointer-events-none" />
                  )}
                </button>

                <h1 className="text-lg md:text-xl font-bold text-[#0A5F38] tracking-wide">IDEALTRENDZ</h1>
              </div>

              {/* Right side - Can add user menu here */}
              <div className="text-sm font-semibold text-slate-500 hidden md:block">
                Employee Portal
              </div>
            </div>
          </div>

          {/* Page Content - Responsive */}
          <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <div className="p-4 md:p-6 lg:p-8">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesLayout;
